/**
 * SecondSold 채팅 시스템 TanStack Query 훅
 *
 * 폴링 기반 실시간 업데이트를 제공하는 React Query 훅들입니다.
 * WebSocket 대신 HTTP 폴링을 사용하여 실시간성을 구현합니다.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { aclosetApi } from '@/app/apis/acloset/acloset-api';
import { resizeImage } from '@/utils/imageUtil';

import type { CreateSecondSoldChatRoomRequest, SendSecondSoldMessageRequest } from '../SecondSoldChat';
import { parseChatRoomKey } from '../helpers/SecondSoldChatHelper';

/**
 * 채팅방 목록 조회 훅 (무한 스크롤 + 폴링 기반)
 *
 * Infinite Query를 사용하여 페이지네이션을 지원하며,
 * 3초마다 새로운 채팅방과 메시지 업데이트를 확인합니다.
 * 탭이 비활성화된 경우 폴링을 중단하여 성능을 최적화합니다.
 *
 * @param options 선택적 조회 옵션 (채팅방 타입 필터링 등)
 */
export const useSecondSoldChatRooms = (options?: {
  /** 특정 채팅방 타입만 조회 */
  chatRoomType?: 'CHAT_ROOM-TYPE_NORMAL-SELLER-MARKET-ITEM' | 'CHAT_ROOM-TYPE_PROSELLER';
  /** 조회할 채팅방 개수 제한 (페이지당) */
  limit?: number;
}) => {
  return useInfiniteQuery({
    queryKey: ['secondSoldChatRooms', options],
    queryFn: async ({ pageParam }) => {
      const response = await aclosetApi.chats.roomsList({
        limit: options?.limit || 20, // 페이지당 채팅방 개수 (무한스크롤용으로 줄임)
        chatRoomType: options?.chatRoomType,
        notEmptyOnly: true,
        cursor: pageParam || undefined, // 첫 페이지는 cursor 없이 요청
      });
      return {
        ...response.data,
        rooms: response.data.rooms?.map(room => ({
          ...room,
          ...parseChatRoomKey(room), // 채팅방 키 파싱하여 ID 추출
        })),
      };
    },
    // 첫 페이지 파라미터 (빈 문자열로 시작)
    initialPageParam: '',
    // 다음 페이지 커서 결정
    getNextPageParam: lastPage => {
      if (lastPage.pagination?.hasNext && lastPage.pagination.nextCursor) {
        return lastPage.pagination.nextCursor;
      }
      return undefined;
    },
    // 3초마다 폴링하여 새로운 채팅방 확인
    refetchInterval: 3000,
    // 브라우저 탭이 비활성화되면 폴링 중단 (성능 최적화)
    refetchIntervalInBackground: false,
    // 1분 후 데이터를 stale로 간주
    staleTime: 60 * 1000,
    // 5분간 캐시 유지
    gcTime: 5 * 60 * 1000,
    // 네트워크 재연결 시 자동 재조회
    refetchOnReconnect: true,
    // 윈도우 포커스 시 재조회 (선택사항)
    refetchOnWindowFocus: false,
  });
};

/**
 * 특정 채팅방의 메시지 목록 조회 훅 (무한 스크롤 + 폴링 기반)
 *
 * Infinite Query를 사용하여 페이지네이션을 지원하며,
 * 2초마다 새로운 메시지를 확인합니다.
 * 채팅방이 선택된 경우에만 쿼리가 활성화됩니다.
 * 스크롤이 상단에 닿으면 이전 메시지들을 로드할 수 있습니다.
 *
 * @param roomID 채팅방 ID (0인 경우 쿼리 비활성화)
 */
export const useSecondSoldMessages = (
  roomID: number,
  options?: {
    pause?: boolean; // 쿼리 일시 중지 옵션
  },
) => {
  return useInfiniteQuery({
    queryKey: ['secondSoldMessages', roomID],
    queryFn: async ({ pageParam }) => {
      const response = await aclosetApi.chats.roomsChatsList({
        roomId: roomID,
        limit: 20,
        cursor: pageParam || undefined, // 첫 페이지는 cursor 없이 요청
        withSeenMark: true,
      });
      return response.data;
    },
    // 첫 페이지 파라미터 (빈 문자열로 시작)
    initialPageParam: '',
    // 다음 페이지 커서 결정 (이전 메시지들을 위한 커서)
    getNextPageParam: lastPage => {
      if (lastPage.pagination.hasNext && lastPage.pagination.nextCursor) {
        return lastPage.pagination.nextCursor;
      }
      return undefined;
    },
    // 5초마다 새 메시지 확인 (채팅방 목록보다 짧은 간격)
    refetchInterval: options?.pause ? false : 5000,
    refetchIntervalInBackground: false,
    // 메시지는 60초 후 stale로 간주 (더 빈번한 업데이트 필요)
    staleTime: 60 * 1000,
    // 10분간 캐시 유지
    gcTime: 10 * 60 * 1000,
    // 채팅방이 선택된 경우에만 쿼리 활성화
    enabled: roomID > 0,
    refetchOnReconnect: options?.pause !== true,
    refetchOnWindowFocus: options?.pause !== true,
  });
};

/**
 * 특정 채팅방 상세 정보 조회 훅
 *
 * roomID를 사용하여 특정 채팅방의 상세 정보를 직접 조회합니다.
 * 무한스크롤 데이터에서 검색하는 것보다 효율적입니다.
 *
 * @param roomID 조회할 채팅방 ID (0인 경우 쿼리 비활성화)
 */
export const useSecondSoldChatRoomDetail = (roomID: number) => {
  return useQuery({
    queryKey: ['secondSoldChatRoomDetail', roomID],
    queryFn: async () => {
      const response = await aclosetApi.chats.roomsList({
        roomID: [roomID], // 특정 room ID로 필터링
        limit: 1, // 단일 채팅방만 조회
      });

      // 첫 번째 채팅방 반환 (단일 결과이므로)
      const room = response.data.rooms?.[0];
      return room
        ? {
            ...room,
            ...parseChatRoomKey(room),
          }
        : null;
    },
    // 채팅방 상세 정보는 자주 변경되지 않으므로 적당한 캐시 시간
    staleTime: 2 * 60 * 1000, // 2분
    gcTime: 10 * 60 * 1000, // 10분
    // roomID가 유효한 경우에만 쿼리 실행
    enabled: roomID > 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

/**
 * 읽지 않은 채팅 수 조회 훅
 *
 * 전체 읽지 않은 메시지 수를 조회합니다.
 * 헤더나 네비게이션의 배지 표시에 사용됩니다.
 */
export const useSecondSoldUnreadCount = () => {
  return useQuery({
    queryKey: ['secondSoldUnreadCount'],
    queryFn: async () => {
      const response = await aclosetApi.chats.unreadChatCountList({});
      return response.data;
    },
    // 5초마다 읽지 않은 메시지 수 확인
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
    // 읽지 않은 수는 즉시 업데이트되어야 함
    staleTime: 0,
    gcTime: 2 * 60 * 1000,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true, // 윈도우 포커스 시 읽지 않은 수 업데이트
  });
};

/**
 * 사용자 프로필 조회 훅
 *
 * userProfileList API를 사용하여 사용자 정보(닉네임, 프로필 이미지 등)를 조회합니다.
 * 채팅방에서 상대방 정보 표시에 사용됩니다.
 *
 * @param userID 조회할 사용자 ID
 * @param options 추가 옵션
 */
export const useSecondSoldUserProfile = (
  userID?: number,
  options?: {
    /** 프로셀러 정보 포함 여부 */
    withProSellerInfo?: boolean;
  },
) => {
  return useQuery({
    queryKey: ['secondSoldUserProfile', userID, options],
    queryFn: async () => {
      if (!userID) {
        throw new Error('User ID is required');
      }

      const response = await aclosetApi.userProfile.userProfileList({
        userID,
        withProSellerInfo: options?.withProSellerInfo || false,
      });
      return response.data;
    },
    // 사용자 프로필은 자주 변경되지 않으므로 긴 캐시 시간
    staleTime: 10 * 60 * 1000, // 10분
    gcTime: 30 * 60 * 1000, // 30분
    // userID가 있을 때만 쿼리 실행
    enabled: !!userID && userID > 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

/**
 * 채팅방 생성 뮤테이션
 *
 * 새로운 채팅방을 생성하고 채팅방 목록을 업데이트합니다.
 */
export const useCreateSecondSoldChatRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: Parameters<typeof aclosetApi.chats.roomsCreate>[0]) => {
      const response = await aclosetApi.chats.roomsCreate(request);
      return response.data;
    },
    onSuccess: () => {
      // 모든 채팅방 목록 쿼리 새로고침 (서버의 최신 상태 반영)
      void queryClient.invalidateQueries({
        queryKey: ['secondSoldChatRooms'],
        // 옵션이 있는 쿼리들도 모두 무효화
        exact: false,
      });
    },
    onError: error => {
      console.error('채팅방 생성 실패:', error);
    },
  });
};

/**
 * 메시지 전송 뮤테이션
 *
 * 메시지를 전송하고 성공 시 관련 쿼리를 새로고침합니다.
 * 버튼 로딩 상태로 전송 진행 상황을 표시합니다.
 */
export const useSecondSoldSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roomID, ...request }: SendSecondSoldMessageRequest) => {
      const response = await aclosetApi.chats.roomsChatsCreate(roomID, {
        contentType: (request.messageType === 'image' ? 'CHAT_CONTENT-TYPE_IMAGE' : 'CHAT_CONTENT-TYPE_TEXT') as any,
        contents: request.content || request.fileURL || '',
        metadata: request.metadata,
      });
      return response.data;
    },

    // 전송 성공 시 관련 쿼리 새로고침
    onSuccess: (_, { roomID }) => {
      // 메시지 목록 새로고침
      void queryClient.invalidateQueries({ queryKey: ['secondSoldMessages', roomID] });
      // 채팅방 목록 새로고침 (마지막 메시지 정보 업데이트)
      void queryClient.invalidateQueries({
        queryKey: ['secondSoldChatRooms'],
        exact: false,
      });
      // 읽지 않은 메시지 수 새로고침
      void queryClient.invalidateQueries({ queryKey: ['secondSoldUnreadCount'] });
    },

    // 전송 실패 시 에러 로깅
    onError: error => {
      console.error('메시지 전송 실패:', error);
    },
  });
};

/**
 * 이미지 업로드 및 전송 뮤테이션
 *
 * 1. pre-signed URL 생성
 * 2. S3에 이미지 업로드
 * 3. 이미지 메시지 전송
 */
export const useSecondSoldSendImageMessage = () => {
  const sendMessageMutation = useSecondSoldSendMessage();

  return useMutation({
    mutationFn: async ({ roomID, file }: { roomID: number; file: File }) => {
      const downsizedFile = await resizeImage(file, {
        width: 1280,
        height: 1280,
        fit: 'scaleDown',
        format: 'image/jpeg',
        quality: 0.7,
        cropPadding: true,
      });

      // 1. pre-signed URL 생성
      const presignedResponse = await aclosetApi.file.presignedCreate({
        category: 'chat/90d',
        contentType: downsizedFile.file.type,
      });

      const { putUrl, getUrl } = presignedResponse.data;

      if (!putUrl || !getUrl) {
        throw new Error('Pre-signed URL 생성에 실패했습니다.');
      }

      // 2. S3에 파일 업로드
      const uploadResponse = await fetch(putUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': downsizedFile.file.type,
        },
        body: downsizedFile.file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`파일 업로드에 실패했습니다: ${uploadResponse.status}`);
      }

      return sendMessageMutation.mutateAsync({
        roomID,
        messageType: 'image',
        fileURL: getUrl,
        metadata: JSON.stringify({
          width: downsizedFile.width,
          height: downsizedFile.height,
          size: downsizedFile.file.size,
          contentType: downsizedFile.file.type,
        }),
      });
    },
    onError: error => {
      console.error('이미지 전송 실패:', error);
    },
  });
};
